import { DeliveryLine } from '../types/stock.types';

/**
 * Material lines, grouped by the product's category (Ops, Oct 2026).
 *
 * The category lives on the PRODUCT — it is the Product Library's field, not something a
 * line carries — and it is the same string a product was filed under when the line was
 * added. Grouping is presentation only: no line's state, lock, audit or count depends on
 * it, so a product re-categorised in the library only changes how an old sheet reads.
 *
 * Shared by the delivery page's material list and the pick sheet, so the two screens
 * group identically — a picker who sees "Scaffolding" second on the delivery sees it
 * second on the sheet.
 */

/** Products filed under no category at all — sorted last, never first. */
export const UNCATEGORISED_LINES = 'Uncategorised';

export interface LineGroup {
    category: string;
    lines: DeliveryLine[];
}

/** The heading a line files under: its product's category, or the catch-all. */
export const lineCategory = (line: DeliveryLine): string =>
    line.product?.category?.trim() || UNCATEGORISED_LINES;

/**
 * Groups in alphabetical order with the catch-all last; the lines inside a group keep the
 * order the server sent them in, which is the order of the paper sheet. So grouping adds
 * headings without shuffling the rack walk.
 */
export function groupLinesByCategory(lines: DeliveryLine[]): LineGroup[] {
    // Dynamic membership (a category only exists if a line carries it) — a Set, then one
    // filter per category. A sheet is tens of lines, so the clear shape wins over a
    // bucket map.
    const categories = Array.from(new Set(lines.map(lineCategory))).sort((a, b) => {
        if (a === UNCATEGORISED_LINES) return 1;
        if (b === UNCATEGORISED_LINES) return -1;
        return a.localeCompare(b);
    });

    return categories.map((category) => ({
        category,
        // Filtered from the original array, so the lines inside a group keep the server's
        // order (the order of the paper sheet).
        lines: lines.filter((line) => lineCategory(line) === category),
    }));
}
